"use client";

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Alert, AlertTitle, Box } from "@mui/material";

import { LookupContextType } from "@/types/lookup";
import { RecaptchaResponse } from "@/types/recaptcha";
import { QuestionRecord } from "@/types/securitySettings";
import { UserBase } from "@/types/user";
import { LookupValidation, ValidationError } from "@/types/validation";

import { getErrorMessage } from "@/lib/data";
import { httpRequest } from "@/lib/http";

import { useRecaptcha } from "@/context/util/recaptcha";
import { useUi } from "@/context/util/ui";

const LookupContext = createContext<LookupContextType>(null!);

export function LookupProvider({ children }: { children: ReactNode }) {
  const {
    needsBackgroundColorUpdate,
    setBackgroundColor,
    setLoading,
    setSnackbar,
  } = useUi();
  const { recaptchaRequest } = useRecaptcha();

  const [alert, setAlert] = useState<ReactNode | null>(null);
  const [errors, setErrors] = useState<ValidationError<LookupValidation>>({});
  const [idSuccess, setIdSuccess] = useState<boolean>(false);
  const [pinSuccess, setPinSuccess] = useState<boolean>(false);
  const [question, setQuestion] = useState<QuestionRecord | null>(null);
  const [lookupUser, setLookupUser] = useState<UserBase | null>(null);

  useEffect(() => {
    const newBackgroundColor = "#f8f8f8";
    if (needsBackgroundColorUpdate(newBackgroundColor))
      setBackgroundColor(newBackgroundColor);
  }, [needsBackgroundColorUpdate, setBackgroundColor]);

  const getUserId = useCallback(() => {
    const request = async (init?: RequestInit) => {
      setLoading(true);

      try {
        const url = `/account-utilities/api/lookup/id`;

        const requestInit: RequestInit = {
          body: JSON.stringify(lookupUser),
          headers: { ...(init?.headers ?? {}) },
          method: "POST",
        };

        const idResponse = (
          await httpRequest<{ message: string } | RecaptchaResponse>(
            url,
            requestInit
          )
        ).data;

        if (!idResponse) throw new Error("Empty http response");

        if ("valid" in idResponse) return idResponse;

        setSuccessAlert(idResponse.message, "username and id");

        setIdSuccess(true);

        setSnackbar((prev) => ({
          ...prev,
          message: "Account ID found",
          open: true,
          severity: "success",
          autoHideDuration: 10000,
        }));
      } catch (error) {
        const message = "Failed to get account ID. Please try again later.";
        console.error(message, error);

        setErrorAlert(error);

        setIdSuccess(false);

        setSnackbar((prev) => ({
          ...prev,
          message,
          open: true,
          severity: "error",
        }));
      } finally {
        setLoading(false);
      }
    };

    return recaptchaRequest("id_lookup", request);
  }, [lookupUser, recaptchaRequest]);

  const getUserPin = useCallback(() => {
    const request = async (init?: RequestInit) => {
      setLoading(true);

      try {
        const url = `/account-utilities/api/lookup/pin`;

        const requestInit: RequestInit = {
          body: JSON.stringify(lookupUser),
          headers: { ...(init?.headers ?? {}) },
          method: "POST",
        };

        const pinResponse = (
          await httpRequest<{ message: string } | RecaptchaResponse>(
            url,
            requestInit
          )
        ).data;

        if (!pinResponse) throw new Error("Empty http response");

        if ("valid" in pinResponse) return pinResponse;

        setSuccessAlert(pinResponse.message, "user pin");

        setPinSuccess(true);

        setSnackbar((prev) => ({
          ...prev,
          message: "Account pin found",
          open: true,
          severity: "success",
          autoHideDuration: 10000,
        }));
      } catch (error) {
        const message = "Failed to get account pin. Please try again later.";
        console.error(message, error);

        setErrorAlert(error);

        setPinSuccess(false);

        setSnackbar((prev) => ({
          ...prev,
          message,
          open: true,
          severity: "error",
        }));
      } finally {
        setLoading(false);
      }
    };

    return recaptchaRequest("pin_lookup", request);
  }, [lookupUser, recaptchaRequest]);

  const getUserSecurityQuestion = useCallback(() => {
    const request = async (init?: RequestInit) => {
      try {
        const url = `/account-utilities/api/security/question`;

        const request = {
          body: JSON.stringify(lookupUser),
          headers: { ...(init?.headers ?? {}) },
          method: "POST",
        };

        const question = (
          await httpRequest<QuestionRecord | RecaptchaResponse>(url, request)
        ).data;

        if (!question) throw new Error("Empty http response");

        if ("valid" in question) return question;

        setQuestion(question);
      } catch (error) {
        const message =
          "Failed to get user's security question. Please try again later.";
        console.error(message, error);

        setSnackbar((prev) => ({
          ...prev,
          message,
          open: true,
          severity: "error",
        }));
      }
    };

    return recaptchaRequest("user_question_lookup", request);
  }, [lookupUser, recaptchaRequest]);

  function setErrorAlert(error: unknown) {
    setAlert(
      <Alert severity="error">
        <AlertTitle>{getErrorMessage(error)}</AlertTitle>
        Please verify the fields are correct.
        <br />
        <br />
        If you are still experiencing this issue, call the Technology Help Line
        at <a href="tel:+18772303509">877-230-3509</a>&nbsp; or email{" "}
        <a href="mailto:askit@morainepark.edu">AskIT@morainepark.edu</a>{" "}
        describing this issue and someone will assist you as soon as possible.
      </Alert>
    );
  }

  function setSuccessAlert(message: string, entity: string) {
    setAlert(
      <Alert severity="success">
        <AlertTitle>{message}</AlertTitle>
        If you are unable to locate an email with your {entity}, please call the
        Technology Help Line at <a href="tel:+18772303509">877-230-3509</a>
        &nbsp; or email{" "}
        <a href="mailto:askit@morainepark.edu">AskIT@morainepark.edu</a>{" "}
        describing this issue and someone will assist you as soon as possible.
      </Alert>
    );
  }

  return (
    <LookupContext.Provider
      value={{
        errors,
        getUserId,
        getUserPin,
        getUserSecurityQuestion,
        idSuccess,
        pinSuccess,
        question,
        setErrors,
        setLookupUser,
        lookupUser,
      }}
    >
      <Box mt={2} sx={{ width: "100%" }}>
        {alert}
        {children}
      </Box>
    </LookupContext.Provider>
  );
}

export function useLookup() {
  const ctx = useContext(LookupContext);
  if (!ctx) throw new Error("useLookup must be used within a LookupContext");
  return ctx;
}
